/**
 * Which arrival deadline is actually in play.
 *
 * A target time on its own is not enough: 8:10 at school means nothing on a Saturday, and it
 * means nothing at 9 AM once the trains that could have met it have gone. Both questions are
 * answered here, and both are pure so they can be tested at any hour of any day.
 */

import { epochAtMinutes, gtfsWeekdayIndex, serviceDateKey } from "../shared/time";
import type { CommutePlan, TargetWindow } from "../shared/types";

/** GTFS weekday indices: 0 = Monday .. 6 = Sunday. School runs Monday to Friday. */
export const DEFAULT_TARGET_DAYS = [0, 1, 2, 3, 4];

/** An arrival still worth planning against this long after the window has closed. */
export const TARGET_GRACE_MS = 60 * 60_000;

/** Anything past a day boundary, but far enough from it that a DST shift cannot land short. */
const NEXT_DAY_PROBE_MS = 25 * 3_600_000;

export function normalizeTargetDays(days: number[] | null | undefined): number[] {
	const valid = (days ?? []).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
	return valid.length > 0 ? [...new Set(valid)].sort((a, b) => a - b) : DEFAULT_TARGET_DAYS;
}

export interface TargetWindowInput {
	stationId: string;
	/** Where the rider actually ends up, when that is not the station. */
	label: string | null;
	targetMinutes: number;
	earlyToleranceMinutes: number;
	lateToleranceMinutes: number;
	/** Days the rider commutes. */
	days: number[];
	nowMs: number;
	timezone: string;
	/** The window has to end after this instant, so a spent morning rolls to the next day. */
	earliestMs: number;
}

/**
 * The next window the rider is actually trying to hit.
 *
 * Rolls forward over days off and over a window that has already closed, so opening the app on
 * a Saturday afternoon plans Monday rather than replaying this morning or inventing Sunday.
 */
export function resolveTargetWindow(input: TargetWindowInput): TargetWindow {
	const { targetMinutes, lateToleranceMinutes, timezone } = input;
	const days = normalizeTargetDays(input.days);
	const lateMs = lateToleranceMinutes * 60_000;

	let targetMs = epochAtMinutes(input.nowMs, targetMinutes, timezone);
	// Seven hops is enough to reach any weekday from any other; the guard is only a backstop.
	for (let attempt = 0; attempt < 7; attempt += 1) {
		const stillAhead = targetMs + lateMs >= input.earliestMs;
		if (stillAhead && days.includes(gtfsWeekdayIndex(serviceDateKey(targetMs, timezone)))) {
			break;
		}
		targetMs = epochAtMinutes(targetMs + NEXT_DAY_PROBE_MS, targetMinutes, timezone);
	}

	return {
		stationId: input.stationId,
		label: input.label,
		targetMs,
		startMs: targetMs - input.earlyToleranceMinutes * 60_000,
		endMs: targetMs + lateMs,
	};
}

/** Late trains in a row that mean the day is lost rather than merely tight. */
export const MISSED_TRAIN_LIMIT = 2;

/**
 * True when the deadline can no longer be met.
 *
 * Counted in trains rather than minutes: one late train is a bad morning that the rider may
 * still want ranked, but the second one in a row says the arrival time is gone, and the useful
 * answer becomes the next school day.
 */
export function missedTheWindow(options: CommutePlan[]): boolean {
	const catchable = options.filter((plan) => plan.feasible);
	if (catchable.length < MISSED_TRAIN_LIMIT) {
		return false;
	}
	return catchable.slice(0, MISSED_TRAIN_LIMIT).every((plan) => plan.status === "late");
}
